import _get from 'lodash/get';
import {
  AppUserAvailableTasksQuery,
  AppUserAvailableTasksQueryVariables,
  Asset,
  FilterGroupingEnum,
  Site,
  SiteArea,
  Task,
  TaskFilterInputObject,
  TaskScopeNameEnum,
} from 'graphql-common';
import QrCodePrefix from '@lib/enums/qrCodePrefix';
import { ErrorPageStatusEnum } from '@lib/components/ErrorPage/enums';
import TasksFilterFieldNames from '@lib/enums/fieldNames/tasksFilterFieldNames';
import { APP_URLS } from 'constants/urls';
import isErrorTimeoutError from '@lib/utils/isErrorTimeoutError';
import { logger } from '@lib/services/logger';
import handleValidCodeForTasksActionFromIndexedDB from './handleValidCodeForTasksActionFromIndexedDB';
import { ScannerActionType } from '../enums';
import { CodeType } from './getCodeType';
import { ParsedCodes } from './getParsedCodes';
import { HandleQrCodeSuccessProps } from './types';

const defaultVariables: AppUserAvailableTasksQueryVariables = {
  page: 1,
  limit: 2,
  taskScope: {
    name: TaskScopeNameEnum.MyTodoAvailableToComplete,
  },
  taskFilters: undefined,
  siteFindBy: { code: undefined },
  assetsFilters: [],
  siteAreaFindBy: { code: undefined },
};

export function getFiltersAndVariables(
  parsedCodes: ParsedCodes,
  codeType: CodeType,
) {
  const filters: TaskFilterInputObject[] = [];
  const { customPrefix, entityCode, sideCode } = parsedCodes;

  const variables: AppUserAvailableTasksQueryVariables = {
    ...defaultVariables,
    siteFindBy: { code: undefined },
    siteAreaFindBy: { code: undefined },
  };

  if (codeType !== CodeType.Invalid) {
    if (sideCode) {
      variables.siteFindBy = { code: sideCode };
      filters.push({
        siteCode: {
          grouping: FilterGroupingEnum.And,
          predicate: { in: [sideCode] },
          filterGroup: 'siteAreaAssetFilerGroup',
        },
      });
    }
    if (entityCode) {
      variables.siteAreaFindBy = { code: entityCode };
      variables.assetsFilters = [
        {
          code: {
            grouping: FilterGroupingEnum.And,
            predicate: {
              eq: entityCode,
            },
          },
        },
        {
          siteCode: {
            grouping: FilterGroupingEnum.And,
            predicate: {
              eq: sideCode,
            },
          },
        },
      ];

      if (codeType === CodeType.SiteArea) {
        filters.push({
          siteAreaCode: {
            grouping: FilterGroupingEnum.And,
            predicate: { in: [entityCode] },
            filterGroup: 'siteAreaAssetFilerGroup',
          },
        });
        filters.push({
          assetId: {
            grouping: FilterGroupingEnum.And,
            predicate: { null: true },
            filterGroup: 'siteAreaAssetFilerGroup',
          },
        });
      }
      if (codeType === CodeType.Asset) {
        filters.push({
          assetCode: {
            grouping: FilterGroupingEnum.And,
            predicate: { in: [entityCode] },
            filterGroup: 'siteAreaAssetFilerGroup',
          },
        });
      }
      if (
        codeType === CodeType.Asset &&
        customPrefix &&
        customPrefix !== QrCodePrefix.SiteArea &&
        customPrefix !== QrCodePrefix.Asset
      ) {
        filters.push({
          assetQrPrefix: {
            grouping: FilterGroupingEnum.And,
            predicate: { in: [customPrefix] },
            filterGroup: 'siteAreaAssetFilerGroup',
          },
        });
      }
    }
  }

  return { filters, variables };
}

async function handleTimeoutOrOffline(
  data: string,
  props: HandleQrCodeSuccessProps,
  parsedCodes: ParsedCodes,
  codeType: CodeType,
) {
  logger.info('Scanner: tasks - switching to IndexedDB (timeout or offline)', {
    data,
    codeType,
  });
  await handleValidCodeForTasksActionFromIndexedDB(
    data,
    props,
    parsedCodes,
    codeType,
  );
}

function handleSuccessResponse(
  data: string,
  userAvailableData: AppUserAvailableTasksQuery | undefined,
  props: HandleQrCodeSuccessProps,
  parsedCodes: ParsedCodes,
  codeType: CodeType,
) {
  const { setCode, navigate, setLoading, setErrorStatus, onSuccessScanAction } =
    props;
  const { entityCode, sideCode } = parsedCodes;

  const collection = _get(userAvailableData, 'tasks.collection', []) as Task[];
  const site = _get(userAvailableData, 'site') as Site;
  const siteArea = _get(userAvailableData, 'siteArea') as SiteArea;
  const asset = _get(userAvailableData, ['assets', 'collection', 0]) as Asset;

  logger.debug('Scanner: tasks - API response', {
    tasksCount: collection.length,
    hasSite: !!site,
    hasSiteArea: !!siteArea,
    hasAsset: !!asset,
    codeType,
  });

  setLoading(false);

  if (codeType === CodeType.Asset && !asset) {
    logger.warn('Scanner: tasks - asset not found for Asset code type');
    setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
    return;
  }

  if (collection.length) {
    setCode(data);
  }

  if (collection.length === 1) {
    logger.info('Scanner: tasks - single task found, navigating', {
      taskId: collection[0].id,
    });
    navigate(
      APP_URLS.app.tasks.completionCreate.getDynamicPath({
        pathParts: { id: collection[0].id },
      }),
    );
  } else if ((sideCode || entityCode) && (site || siteArea || asset)) {
    // When we're prefilling SiteArea, skip Site — an area already implies
    // its parent site, so adding the site filter is redundant noise.
    const willPrefillArea = !!(codeType === CodeType.SiteArea && siteArea);
    const values = {
      [TasksFilterFieldNames.Site]:
        site && !willPrefillArea
          ? [{ value: site.id, label: site.name }]
          : undefined,
      [TasksFilterFieldNames.SiteArea]: willPrefillArea
        ? [{ value: siteArea.id, label: siteArea.name }]
        : undefined,
      [TasksFilterFieldNames.Asset]:
        codeType === CodeType.Asset && asset
          ? [{ value: asset.id, label: asset.name }]
          : undefined,
    };
    logger.info('Scanner: tasks - navigating to task list with filters', {
      siteId: site?.id,
      siteName: site?.name,
      siteAreaId: siteArea?.id,
      siteAreaName: siteArea?.name,
      assetId: asset?.id,
      assetName: asset?.name,
    });
    onSuccessScanAction(values, ScannerActionType.FindTasks);
  } else {
    logger.warn('Scanner: tasks - no matching tasks or entities found');
    setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
  }
}

export default async function handleValidCodeForTasksAction(
  data: string,
  props: HandleQrCodeSuccessProps,
  parsedCodes: ParsedCodes,
  codeType: CodeType,
  isOnline: boolean,
) {
  const { setLoading, fetchAppTasks, setErrorStatus } = props;
  try {
    if (!isOnline) {
      logger.info('Scanner: tasks - offline mode detected');
      await handleTimeoutOrOffline(data, props, parsedCodes, codeType);
      return;
    }

    const { filters, variables } = getFiltersAndVariables(
      parsedCodes,
      codeType,
    );
    if (filters.length) {
      variables.taskFilters = filters;
    }

    setLoading(true);
    const res = await fetchAppTasks({ variables, errorPolicy: 'ignore' });

    const { data: userAvailableData, error } = res;
    const isTimeoutError = isErrorTimeoutError(error);

    if (isTimeoutError) {
      logger.warn('Scanner: tasks - API timeout, falling back to IndexedDB');
      await handleTimeoutOrOffline(data, props, parsedCodes, codeType);
    } else if (error) {
      logger.error('Scanner: tasks - API error', {
        error: error.message,
      });
      setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
    } else {
      handleSuccessResponse(
        data,
        userAvailableData,
        props,
        parsedCodes,
        codeType,
      );
    }
  } catch (error) {
    logger.error('Scanner: tasks - unexpected error', {
      error: error instanceof Error ? error.message : String(error),
    });
    setErrorStatus(ErrorPageStatusEnum.QrCodeScanFailed);
  }
}
